import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Users, Plus, Edit2, Shield, UserX, UserCheck, Mail, Phone, Lock, Search, X } from 'lucide-react';
import PasswordStrength from '@/components/auth/PasswordStrength';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', role: 'agent' });
  const { user: me } = useAuth();
  const meRole = (me?.role || '').toLowerCase();
  const meIsAdmin = meRole === 'admin';
  const meIsHr = meRole === 'hr';
  const roleOptions = meIsAdmin ? ['agent', 'accounts', 'admin', 'hr'] : ['agent', 'accounts'];

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/users`);
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/users`, form);
      toast.success('User created!');
      setShowAddModal(false);
      setForm({ name: '', email: '', password: '', phone: '', role: 'agent' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = { name: form.name, phone: form.phone, role: form.role };
      if (form.password) updateData.password = form.password;
      await axios.put(`${BACKEND_URL}/api/users/${editingUser.user_id}`, updateData);
      toast.success('User updated!');
      setEditingUser(null);
      setForm({ name: '', email: '', password: '', phone: '', role: 'agent' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      if (user.is_active === false) {
        await axios.put(`${BACKEND_URL}/api/users/${user.user_id}`, { is_active: true });
        toast.success('User activated');
      } else {
        await axios.delete(`${BACKEND_URL}/api/users/${user.user_id}`);
        toast.success('User deactivated');
      }
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({ name: user.name, email: user.email, password: '', phone: user.phone || '', role: user.role });
    setShowAddModal(false);
  };

  const openAdd = () => {
    setEditingUser(null);
    setForm({ name: '', email: '', password: '', phone: '', role: 'agent' });
    setShowAddModal(true);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
            <Users className="h-8 w-8 text-primary-700" />Team Management
          </h1>
          <p className="text-slate-500 mt-1">{users.length} team member{users.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={openAdd} className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl shadow-lg" data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-2" />Add Agent
        </Button>
      </div>

      <Card className="p-4 bg-white/80 backdrop-blur-xl rounded-2xl">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search team members..." className="pl-12 h-12 rounded-xl" data-testid="search-users" />
        </div>
      </Card>

      <div className="grid gap-4">
        {filtered.map((user) => (
          <Card key={user.user_id} className="p-5 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-testid={`user-card-${user.user_id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold ${user.role === 'admin' ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-gradient-to-br from-teal-500 to-emerald-600'}`}>
                  {(user.name || '?')[0].toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900">{user.name}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${user.role === 'admin' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'}`}>
                      {user.role}
                    </span>
                    {user.is_active === false && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-red-100 text-red-700">Inactive</span>
                    )}
                    {user.auth_provider === 'google' && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-blue-100 text-blue-700">Google</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{user.email}</span>
                    {user.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{user.phone}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {meIsHr && (user.role === 'admin' || user.role === 'hr') ? (
                  <span className="text-[10px] text-slate-300 uppercase tracking-wide px-2" data-testid={`user-locked-${user.user_id}`}>Admin-managed</span>
                ) : (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(user)} className="h-9 w-9 p-0 text-slate-500 hover:text-teal-600" data-testid={`edit-user-${user.user_id}`}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleToggleActive(user)} className={`h-9 w-9 p-0 ${user.is_active === false ? 'text-emerald-500 hover:text-emerald-700' : 'text-red-400 hover:text-red-600'}`} data-testid={`toggle-user-${user.user_id}`}>
                      {user.is_active === false ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingUser) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4" data-testid="user-modal">
          <Card className="w-full max-w-md p-6 bg-white rounded-3xl shadow-2xl relative">
            <button onClick={() => { setShowAddModal(false); setEditingUser(null); }} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
              <X className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              {editingUser ? <Edit2 className="h-5 w-5 text-teal-600" /> : <Plus className="h-5 w-5 text-teal-600" />}
              {editingUser ? 'Edit User' : 'Add New Agent'}
            </h2>
            <form onSubmit={editingUser ? handleEditUser : handleAddUser} className="space-y-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1 block">Full Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl" required data-testid="modal-input-name" />
              </div>
              {!editingUser && (
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl" required data-testid="modal-input-email" />
                </div>
              )}
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1 block">{editingUser ? 'New Password (leave blank to keep)' : 'Password'}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 rounded-xl" required={!editingUser} data-testid="modal-input-password" />
                {form.password && <PasswordStrength password={form.password} />}
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1 block">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 rounded-xl" data-testid="modal-input-phone" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1 block">Role</Label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} disabled={!!editingUser && meIsHr} className="w-full h-11 rounded-xl border border-slate-200 px-3 bg-white disabled:bg-slate-50 disabled:text-slate-400" data-testid="modal-select-role">
                  {roleOptions.map((r) => (
                    <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                  ))}
                </select>
                {!!editingUser && meIsHr ? (
                  <p className="text-[11px] text-slate-400 mt-1" data-testid="user-role-disabled-note">Only an admin can change a member's role.</p>
                ) : null}
              </div>
              <Button type="submit" className="w-full h-11 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl font-semibold" data-testid="modal-submit-btn">
                {editingUser ? 'Update User' : 'Create Agent'}
              </Button>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
